import { useSize } from '@cibo/ui/src/hooks/useSize'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import * as d3 from 'd3'
import { filter, flatten, groupBy, mapObjIndexed, pipe, pluck, prop, uniq } from 'ramda'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { Swatch } from '../../../../consts/Theme'
import { InventoryCsvRow } from '../../types'

const PALETTE = ['#afcd5f', '#5fa7f1']

type EmissionFactorsByBoundaryProps = {
  height: number
  csvData: Array<InventoryCsvRow>
  boundary?: string
  year: number
}

const PADDING = {
  left: 128,
  top: 0,
  right: 40,
  bottom: 32,
}

export const EmissionFactorsByBoundary = ({
  height,
  csvData,
  boundary,
  year,
}: EmissionFactorsByBoundaryProps) => {
  const { t } = useTranslation('impact/EmissionFactorsByBoundary')
  const theme = useTheme()

  const graphId = useMemo(() => `graph-${uuid()}`, [])
  const sizeTarget = useRef<HTMLElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const size = useSize(sizeTarget, 100)

  const { width } = size || { width: 0 }

  const innerWidth = width ? width - PADDING.left - PADDING.right : 0
  const innerHeight = height - PADDING.bottom - PADDING.top

  const emissionsByCrop = pipe<
    InventoryCsvRow[],
    InventoryCsvRow[],
    Record<string, InventoryCsvRow[]>,
    Record<string, Record<string, InventoryCsvRow[]>>,
    Record<string, Record<string, number>>
  >(
    filter(
      (row: InventoryCsvRow) =>
        (!boundary || row.supplyShed === boundary) &&
        row.emissionsCategory !== 'Removals' &&
        row.emissionsCategory !== 'none' &&
        row.interventionOrSupplyShed !== 'Intervention' &&
        row.year === year
    ),
    groupBy(prop('commodity')),
    mapObjIndexed(groupBy(prop('supplyShed'))),
    mapObjIndexed(
      mapObjIndexed(rows => pluck('kgCO2EquivPerKgTotalYield')(rows).reduce((a, b) => a + b, 0))
    )
  )(csvData)

  const crops: string[] = Object.keys(emissionsByCrop)
  const categories = uniq(
    flatten(Object.values(emissionsByCrop).map(cropValues => Object.keys(cropValues)))
  )

  const allValues = flatten(
    Object.values(emissionsByCrop).map(cropValues => Object.values(cropValues))
  )

  const colorScale = d3.scaleOrdinal(PALETTE)

  const yScale = useMemo(
    () =>
      d3
        .scaleBand()
        .domain(crops)
        .range([0, innerHeight])
        .paddingOuter(0.25)
        .paddingInner(0.25),
    [crops]
  )

  const categoryScale = useMemo(
    () =>
      d3
        .scaleBand()
        .domain(categories)
        .range([0, yScale.bandwidth()])
        .paddingInner(0.15),
    [yScale]
  )

  const xScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, 1.1 * (d3.max(allValues) || 0.5)])
        .range([0, innerWidth]),
    [allValues]
  )

  useEffect(() => {
    const svg = d3.select(svgRef.current)

    svg
      .selectAll<SVGGElement, any>('g.x.axis')
      .call(
        d3
          .axisBottom(xScale)
          .tickSizeInner(0)
          .tickSizeOuter(0)
      )
      .call(g => g.select('.domain').remove())
      .call(g => {
        g.selectAll('.grid').remove()
        g.selectAll('.tick line')
          .clone()
          .attr('y2', -innerHeight)
          .attr('stroke-opacity', 0.1)
          .attr('class', 'grid')
      })

    svg.selectAll<SVGGElement, any>('g.y.axis').call(
      d3
        .axisLeft(yScale)
        .tickSizeInner(0)
        .tickSizeOuter(0)
        .tickFormat(context => t('crop', { context }))
    )
  }, [svgRef, xScale, yScale])

  return (
    <Box
      ref={sizeTarget}
      sx={{
        'svg text': {
          fontSize: 12,
          fontWeight: 400,
          fill: theme.palette.text.secondary,
        },
        'g.x.axis': {
          transform: `translateY(${innerHeight}px)`,
        },
        'g.x.axis text': {
          transform: `translateY(8px)`,
          textAnchor: 'start',
        },
        'g.y.axis text': {
          transform: `translateX(-8px)`,
        },
        'g.axis path': {
          stroke: theme.palette.text.secondary,
          strokeWidth: 2,
          opacity: 0.3,
        },
      }}
    >
      <svg ref={svgRef} id={graphId} width={width || undefined} height={height}>
        <g transform={`translate(${PADDING.left},${PADDING.top})`}>
          <g className="y axis"></g>
          <g className="yield axis"></g>
          <g className="x axis"></g>

          {Object.entries(emissionsByCrop).map(([crop, categories]) =>
            Object.entries(categories).map(([category, percentage]) => (
              <rect
                key={`${crop}-${category}`}
                y={(yScale(crop) || 0) + (categoryScale(category) || 0)}
                height={categoryScale.bandwidth()}
                width={xScale(percentage)}
                fill={colorScale(category)}
              />
            ))
          )}
        </g>
      </svg>

      <Stack ml={2}>
        <Box>
          {categories.map(category => (
            <span key={category}>
              <div style={{ display: 'inline-block' }}>
                <Stack direction="row" alignItems="center" spacing={1} width="content" mr={2}>
                  <Swatch color={colorScale(category)} />
                  <Typography variant="caption">{category}</Typography>
                </Stack>
              </div>
            </span>
          ))}
        </Box>
      </Stack>
    </Box>
  )
}
